import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import { FullPageSpinnerPortal } from '@/components/common/Spinner/FullPagePortal'
import { useSettings } from '@/hooks/useSettings'
import {
  getApiAuthMeOptions,
  getApiGuestsByIdOptions,
  getApiGuestsOptions,
  postApiCheckinsMutation,
} from '@/lib/api/client/@tanstack/react-query.gen'
import { useAppForm } from '@/lib/forms/hook'
import { useLIFF } from '@/lib/liff/context'

import { ConfirmStep } from './checkin/ConfirmStep'
import { GroupStep } from './checkin/GroupStep'
import { GuestStep } from './checkin/GuestStep'
import { ManualStep } from './checkin/ManualStep'
import { SearchAllStep } from './checkin/SearchAllStep'
import { SideStep } from './checkin/SideStep'
// Step Components
import { ThankYouStep } from './checkin/ThankYouStep'
import type { GuestItem, Side, StepType } from './checkin/types'

// ─── Manual form schema ──────────────────────────────────────────────────────

const checkinSchema = z.object({
  fullName: z.string().min(1, 'กรุณากรอกชื่อเล่น'),
  displayName: z.string().min(1, 'กรุณากรอกชื่อที่ต้องการให้แสดงในงาน'),
  maritalStatus: z.string().min(1, 'กรุณาเลือกสถานะ'),
  followers: z.string().min(1),
  side: z.enum(['groom', 'bride'], {
    message: 'กรุณาเลือกฝ่าย',
  }),
})

type CheckinFormValues = z.infer<typeof checkinSchema>

// ─── Main component ───────────────────────────────────────────────────────────

interface CheckinPageProps {
  checkinId?: string
}

export function CheckinPage({ checkinId }: CheckinPageProps) {
  const { profile } = useLIFF()
  const queryClient = useQueryClient()

  const { data: userMe, isLoading: isMeLoading } = useQuery(
    getApiAuthMeOptions(),
  )
  const { isCheckInOpen, isLoading: isSettingsLoading } = useSettings()

  const { mutate: checkinMutate, isPending: isCheckinPending } = useMutation(
    postApiCheckinsMutation(),
  )

  const { data: scannedGuestData, isLoading: isScannedGuestLoading } = useQuery(
    {
      ...getApiGuestsByIdOptions({ path: { id: checkinId || '' } }),
      enabled: !!checkinId,
    },
  )

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [hasProcessedPreload, setHasProcessedPreload] = useState(false)

  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState<StepType>('side')
  const [selectedSide, setSelectedSide] = useState<Side | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [selectedGuest, setSelectedGuest] = useState<GuestItem | null>(null)
  const [groupSearch, setGroupSearch] = useState('')
  const [guestSearch, setGuestSearch] = useState('')
  const [searchAllQuery, setSearchAllQuery] = useState('')
  const [prevGuestStep, setPrevGuestStep] = useState<
    'side' | 'guest' | 'search-all'
  >('side')

  // ── Fetch guests for selected side ─────────────────────────────────────────
  const { data: sideGuestsData, isFetching: isSideGuestsFetching } = useQuery({
    ...getApiGuestsOptions({
      query: { side: selectedSide ?? undefined, limit: 1000, page: 1 },
    }),
    enabled: !!selectedSide,
  })

  // ── Fetch all guests for search-all step ────────────────────────────────────
  const { data: allGuestsData, isFetching: isAllGuestsFetching } = useQuery({
    ...getApiGuestsOptions({ query: { limit: 1000, page: 1 } }),
    enabled: step === 'search-all',
  })

  const allGuests = useMemo(
    () => (allGuestsData?.data ?? []) as GuestItem[],
    [allGuestsData],
  )

  const filteredAllGuests = useMemo(() => {
    if (!searchAllQuery) return allGuests
    const s = searchAllQuery.toLowerCase()
    return allGuests.filter(
      (g) =>
        g.fullName.toLowerCase().includes(s) ||
        (g.displayName ?? '').toLowerCase().includes(s) ||
        (g.group ?? '').toLowerCase().includes(s),
    )
  }, [allGuests, searchAllQuery])

  const sideGuests = useMemo(
    () => (sideGuestsData?.data ?? []) as GuestItem[],
    [sideGuestsData],
  )

  const distinctGroups = useMemo(() => {
    const groups = new Set<string>()
    sideGuests.forEach((g) => {
      if (g.group) groups.add(g.group)
    })
    return Array.from(groups).sort()
  }, [sideGuests])

  const filteredGroups = useMemo(() => {
    if (!groupSearch) return distinctGroups
    return distinctGroups.filter((g) =>
      g.toLowerCase().includes(groupSearch.toLowerCase()),
    )
  }, [distinctGroups, groupSearch])

  const groupGuests = useMemo(() => {
    if (!selectedGroup) return []
    return sideGuests.filter((g) => g.group === selectedGroup)
  }, [sideGuests, selectedGroup])

  const filteredGuests = useMemo(() => {
    if (!guestSearch) return groupGuests
    const s = guestSearch.toLowerCase()
    return groupGuests.filter(
      (g) =>
        g.fullName.toLowerCase().includes(s) ||
        (g.displayName ?? '').toLowerCase().includes(s),
    )
  }, [groupGuests, guestSearch])

  // ── Checkin handlers ────────────────────────────────────────────────────────

  const handleCheckin = (values?: CheckinFormValues) => {
    if (!isCheckInOpen) {
      setSubmitError('ขออภัย ระบบเช็กอินปิดอยู่ในขณะนี้')
      return
    }
    setSubmitError('')
    checkinMutate(
      {
        body: {
          ...(values || {}),
          followers: values?.followers ? Number(values.followers) : undefined,
          checkinId,
        } as any,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getApiAuthMeOptions().queryKey,
          })
          setIsSubmitted(true)
        },
        onError: (err: any) => {
          setSubmitError(
            err.body?.error || err.message || 'เกิดข้อผิดพลาดในการเช็กอิน',
          )
        },
      },
    )
  }

  const handleMasterGuestCheckin = (
    guestId: string,
    displayName?: string,
    maritalStatus?: string,
  ) => {
    if (!isCheckInOpen) {
      setSubmitError('ขออภัย ระบบเช็กอินปิดอยู่ในขณะนี้')
      return
    }
    setSubmitError('')
    checkinMutate(
      { body: { masterGuestId: guestId, displayName, maritalStatus } as any },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getApiAuthMeOptions().queryKey,
          })
          setIsSubmitted(true)
        },
        onError: (err: any) => {
          setSubmitError(
            err.body?.error || err.message || 'เกิดข้อผิดพลาดในการเช็กอิน',
          )
        },
      },
    )
  }

  // ── Manual form ──────────────────────────────────────────────────────────────
  const form = useAppForm({
    defaultValues: {
      fullName: '',
      displayName: '',
      maritalStatus: '',
      followers: '1',
      side: selectedSide || '',
    } as any,
    validators: { onChange: checkinSchema },
    onSubmit: async ({ value }) => handleCheckin(value),
  })

  // ── Confirm step form ────────────────────────────────────────────────────────
  const confirmForm = useAppForm({
    defaultValues: { displayName: '', maritalStatus: '' },
    validators: {
      onChange: z.object({
        displayName: z.string().min(1, 'กรุณากรอกชื่อที่ต้องการแสดง'),
        maritalStatus: z.string().min(1, 'กรุณาเลือกสถานะ'),
      }),
    },
    onSubmit: async ({ value }) => {
      if (selectedGuest) {
        handleMasterGuestCheckin(
          selectedGuest.id,
          value.displayName,
          value.maritalStatus,
        )
      }
    },
  })

  // ── Already checked-in check ────────────────────────────────────────────────
  const isAlreadyCheckedIn = useMemo(() => {
    if (userMe?.status?.hasCheckedIn) return true

    if (scannedGuestData) {
      const count = scannedGuestData.checkedInCount || 0
      const capacity = scannedGuestData.expectedFollowers || 1
      if (scannedGuestData.status === 'checked-in' && count >= capacity) {
        return true
      }
    }

    if (selectedGuest) {
      const count = selectedGuest.checkedInCount || 0
      const capacity = selectedGuest.expectedFollowers || 1
      if (selectedGuest.status === 'checked-in' && count >= capacity) {
        return true
      }
    }

    return false
  }, [userMe, scannedGuestData, selectedGuest])

  // ── Auto check-in via QR code / Preloaded Guest ─────────────────────────────
  useEffect(() => {
    if (isMeLoading || isSettingsLoading || isCheckinPending || isSubmitted)
      return

    if (profile?.displayName && !form.state.values.displayName) {
      form.setFieldValue('displayName', profile.displayName)
    }

    if (checkinId) {
      if (scannedGuestData) {
        const count = scannedGuestData.checkedInCount || 0
        const capacity = scannedGuestData.expectedFollowers || 1
        const isFullyCheckedIn =
          scannedGuestData.status === 'checked-in' && count >= capacity

        if (isFullyCheckedIn || userMe?.status?.hasCheckedIn) {
          setHasProcessedPreload(true)
          return
        }

        setSelectedGuest({
          id: scannedGuestData.id,
          fullName: scannedGuestData.fullName,
          displayName: scannedGuestData.displayName || null,
          group: scannedGuestData.group || undefined,
          side: scannedGuestData.side as 'groom' | 'bride' | undefined,
          expectedFollowers: scannedGuestData.expectedFollowers,
          checkedInCount: scannedGuestData.checkedInCount,
          status: scannedGuestData.status || 'pending',
          tableAssignment: scannedGuestData.tableAssignment,
        })
        setPrevGuestStep('side')

        confirmForm.setFieldValue(
          'displayName',
          scannedGuestData.displayName || profile?.displayName || '',
        )
        confirmForm.setFieldValue('maritalStatus', '')
        setStep('confirm')
        setHasProcessedPreload(true)
      }
      return
    }

    if (userMe?.status?.hasCheckedIn || !userMe?.preloadedGuest) {
      setHasProcessedPreload(true)
      return
    }

    const pg = userMe.preloadedGuest
    if (pg) {
      if (pg.isMetadataComplete) {
        // Auto check in on frontend using separate route!
        handleMasterGuestCheckin(
          pg.id,
          pg.displayName || undefined,
          pg.maritalStatus || undefined,
        )
      } else {
        setSelectedGuest({
          id: pg.id,
          fullName: pg.fullName,
          displayName: pg.displayName || null,
          group: pg.group || undefined,
          side: pg.side as 'groom' | 'bride' | undefined,
          expectedFollowers: pg.expectedFollowers,
          checkedInCount: pg.checkedInCount,
          status: 'pending',
        })
        setPrevGuestStep('guest')

        confirmForm.setFieldValue(
          'displayName',
          pg.displayName || profile?.displayName || '',
        )
        confirmForm.setFieldValue('maritalStatus', pg.maritalStatus || '')
        setStep('confirm')
      }
      setHasProcessedPreload(true)
    }
  }, [
    userMe,
    checkinId,
    isMeLoading,
    isSettingsLoading,
    isCheckInOpen,
    profile,
    scannedGuestData,
  ])

  // ── Loading & already checked-in screens ────────────────────────────────────

  if (
    isMeLoading ||
    isSettingsLoading ||
    isScannedGuestLoading ||
    (isCheckinPending && step === 'side') ||
    (!hasProcessedPreload &&
      userMe?.preloadedGuest &&
      !userMe?.status?.hasCheckedIn)
  ) {
    return <FullPageSpinnerPortal />
  }

  if (isSubmitted || isAlreadyCheckedIn) {
    const profileData =
      userMe?.profile ||
      (selectedGuest
        ? { fullName: selectedGuest.displayName || selectedGuest.fullName }
        : null) ||
      (scannedGuestData
        ? {
            fullName: scannedGuestData.displayName || scannedGuestData.fullName,
          }
        : null)

    const checkInDetail =
      userMe?.checkInDetail ||
      (scannedGuestData?.tableAssignment
        ? { table: scannedGuestData.tableAssignment.tableNumber }
        : null) ||
      (selectedGuest?.tableAssignment
        ? { table: selectedGuest.tableAssignment.tableNumber }
        : null)

    return (
      <ThankYouStep
        profileData={profileData}
        pictureUrl={profile?.pictureUrl}
        checkInDetail={checkInDetail}
      />
    )
  }

  // ── STEP: side selection ──────────────────────────────────────────────────────
  if (step === 'side') {
    return (
      <SideStep
        onSelectSide={(side) => {
          setSelectedSide(side)
          setSelectedGroup(null)
          setSelectedGuest(null)
          setGroupSearch('')
          setGuestSearch('')
          setStep('group')
        }}
        onSearchAll={() => {
          setSearchAllQuery('')
          setStep('search-all')
        }}
      />
    )
  }

  // ── STEP: group selection ─────────────────────────────────────────────────────
  if (step === 'group') {
    return (
      <GroupStep
        onBack={() => setStep('side')}
        groupSearch={groupSearch}
        onGroupSearchChange={setGroupSearch}
        isFetching={isSideGuestsFetching}
        filteredGroups={filteredGroups}
        sideGuests={sideGuests}
        onSelectGroup={(group) => {
          setSelectedGroup(group)
          setGuestSearch('')
          setStep('guest')
        }}
        onSearchAll={() => {
          setSearchAllQuery('')
          setStep('search-all')
        }}
      />
    )
  }

  // ── STEP: guest selection ─────────────────────────────────────────────────────
  if (step === 'guest') {
    return (
      <GuestStep
        onBack={() => setStep('group')}
        guestSearch={guestSearch}
        onGuestSearchChange={setGuestSearch}
        filteredGuests={filteredGuests}
        onSelectGuest={(guest) => {
          setSelectedGuest(guest)
          setPrevGuestStep('guest')
          confirmForm.reset()
          confirmForm.setFieldValue('displayName', profile?.displayName ?? '')
          setStep('confirm')
        }}
        onManualRegister={() => {
          setPrevGuestStep('guest')
          if (profile?.displayName) {
            form.setFieldValue('displayName', profile.displayName)
          }
          setStep('manual')
        }}
      />
    )
  }

  // ── STEP: search-all ─────────────────────────────────────────────────────────
  if (step === 'search-all') {
    return (
      <SearchAllStep
        onBack={() => setStep('side')}
        searchAllQuery={searchAllQuery}
        onSearchAllQueryChange={setSearchAllQuery}
        isFetching={isAllGuestsFetching}
        filteredAllGuests={filteredAllGuests}
        onSelectGuest={(guest) => {
          setSelectedGuest(guest)
          setPrevGuestStep('search-all')
          confirmForm.reset()
          confirmForm.setFieldValue('displayName', profile?.displayName ?? '')
          setStep('confirm')
        }}
        onManualRegister={() => {
          setPrevGuestStep('search-all')
          if (profile?.displayName) {
            form.setFieldValue('displayName', profile.displayName)
          }
          setStep('manual')
        }}
      />
    )
  }

  // ── STEP: confirm ─────────────────────────────────────────────────────────────
  if (step === 'confirm' && selectedGuest) {
    return (
      <ConfirmStep
        selectedGuest={selectedGuest}
        onBack={() => setStep(prevGuestStep)}
        confirmForm={confirmForm}
        submitError={submitError}
        isCheckinPending={isCheckinPending}
      />
    )
  }

  // ── STEP: manual form ─────────────────────────────────────────────────────────
  return (
    <ManualStep
      onBack={() => setStep(prevGuestStep)}
      form={form}
      submitError={submitError}
      isCheckinPending={isCheckinPending}
    />
  )
}
